import { useMemo, useState } from 'react'

import {
  useDispatch,
  useSelector,
  setSearchDisplayMode,
  resetSelectedSearchResult,
} from '@ggs/store'
import { useI18n } from '@ggs/i18n'

import {
  Divider,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Checkbox,
  FormLabel,
  Typography,
} from '@mui/material'

import { Button } from '../../buttons'
import { DistanceSelectField, LanguageSelectField } from '../../forms/fields'
import { ButtonGroupInput } from '../../inputs'
import { LeafletMap } from '../../maps'
import { ListPager, SelectPager } from '../../pager'
import { EmptyResult, SearchResult, ResultsLoading, SearchResultPopup } from '../../results'

import { transformResultToLeafletPlaces } from '../../../local/utils'
import filtersByDistance from '../../../mock_data/distanceOptions.json'

import './ResultsList.scss'


/**
 * @typedef {object} ResultsListProps
 * @property {boolean} isLoading}
 * @property {import("../../../definitions/Search211ApiTypes").Search211Response} data
 * @property {import("../../../definitions/GoogleMapsApiTypes").GMapsCoords} geoData
 * @property {number} startResult
 * @property {number} endResult
 * @property {string} sort
 * @property {import('react').Dispatch<import('react').SetStateAction<string>>} setSort
 * @property {boolean} removeDigital
 * @property {function} setRemoveDigital
 * @property {number} searchDistance
 * @property {function} onDistanceChange
 * @property {{label: string, value: string}[]} languageOptions
 * @property {string} serviceLanguage
 * @property {function} onLanguageChange
 * @property {number} numPages
 * @property {number} pageIndex
 * @property {function} onPageChange
 */

/**
 * @param {ResultsListProps} props
 * @return {JSX.Element}
 */
const ResultsList = ({
  isLoading,
  data,
  geoData,
  startResult,
  endResult,
  sort,
  setSort,
  removeDigital,
  setRemoveDigital,
  searchDistance,
  onDistanceChange,
  languageOptions,
  serviceLanguage,
  onLanguageChange,
  numPages,
  pageIndex,
  onPageChange,
}) => {
  const { t } = useI18n()

  const displayModeButtonGroupOptions = {
    id: 'display-modes-buttons',
    buttonOptions: [
      {
        label: t('List View'),
        value: 'list',
      },
      {
        label: t('Map View'),
        value: 'map',
      },
    ],
  }

  const { RecordCount: count, Records: records } = data
  const { address, postalCode, lat, lng } = geoData
  const resultLabel = `${
    count > 0 ? `${startResult} - ${endResult} of ${count}` : t('No results')
  } ${t('near')} "${postalCode || address}"`

  const displayMode = useSelector((state) => state.ui.search.displayMode)
  const selectedResultId = useSelector((state) => state.ui.search.selectedResultId)
  const dispatch = useDispatch()

  const [sortFilter, setSortFilter] = useState(sort || 'distance')

  const PAGE_SIZE = 25

  const results = useMemo(() => {
    // the page index must be mod 10 to reference the correct page for the total 250 results
    // e.g. page 11 should be indexed 1 in the new set of 250 results
    const adjustedIndex = pageIndex % 10
    return records.slice(adjustedIndex * PAGE_SIZE, (adjustedIndex + 1) * PAGE_SIZE)
  }, [pageIndex, records])

  // console.log('results list?', results)

  /**
   *
   * @param {React.ChangeEvent<HTMLInputElement>}e
   */
  const handleSortChange = (e) => {
    setSortFilter(e.target.value)
    setSort(e.target.value)
  }

  /**
   * Change the search display mode.
   * @param {string} newDisplayMode
   */
  const changeDisplayMode = (newDisplayMode) => {
    // Ensure clear state when user change display mode.
    dispatch(resetSelectedSearchResult())
    // Activate the selected display mode.
    dispatch(setSearchDisplayMode(newDisplayMode))
  }

  const mapPlaces = useMemo(() => {
    return results
      .map((record, index) => {
        const place = transformResultToLeafletPlaces(record, lat, lng)
        const isPhysical =
          record.PhysicalAddressStreet1 !== '' || record.PhysicalAddressStreet2 !== ''
        return isPhysical
          ? {
            ...place,
            index: startResult + index,
            popupContent: <SearchResultPopup record={record} distance={place.distance} />,
          } : null
      })
      .filter((record) => Boolean(record))
  }, [results])

  // Resolve selected search result record.
  const selectedRecord = useMemo(() => {
    return results.find((record) => record.id === selectedResultId)
  }, [results, selectedResultId])

  if (isLoading) {
    return (
      <div className="results-list">
        <div className="results-list__header">
          <ResultsLoading />
        </div>
      </div>
    )
  }

  if (count === 0 || !records) {
    return (
      <div className="results-list__content">
        <EmptyResult />
      </div>
    )
  }

  return (
    <div className="results-list">
      <div className="results-list__header">
        <div>
          <Typography color="primary" className="results-list__label">
            {resultLabel}
          </Typography>
        </div>
        <ButtonGroupInput
          {...displayModeButtonGroupOptions}
          fullWidth={false}
          changeCallback={changeDisplayMode}
          value={displayMode}
        />
      </div>
      <div className="results-list__subheader">
        <div className="results-list__filters">
          <FormLabel>{t('Sort By: ')}</FormLabel>
          <FormControl>
            <RadioGroup
              row
              className="results-list__sort-by-filter"
              aria-label={t('Sort Search Results')}
              name="search-results-sort-filter"
              value={sortFilter}
              onChange={handleSortChange}
            >
              <FormControlLabel value="best" control={<Radio />} label={t('Best Match')} />
              <FormControlLabel value="distance" control={<Radio />} label={t('Distance')} />
            </RadioGroup>
          </FormControl>
          {displayMode !== 'map' && (
            <FormControl className="results-list__filter-resource">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={removeDigital}
                    color="primary"
                    onChange={(e) => setRemoveDigital(e.target.checked)}
                  />
                }
                label={t('Remove Digital Services')}
              />
            </FormControl>
          )}
        </div>

        <Divider
          orientation="vertical"
          variant="middle"
          flexItem
          classes={{ root: 'results-list__divider' }}
        />

        <div className="results-list__filters results-list__search-filters">
          <FormLabel>{t('Filter By: ')}</FormLabel>
          <div className="results-list__search-filter-fields">
            <DistanceSelectField
              options={filtersByDistance}
              value={searchDistance}
              setValue={onDistanceChange}
            />
            <LanguageSelectField
              options={languageOptions}
              value={serviceLanguage}
              setValue={onLanguageChange}
            />
          </div>

        </div>

      </div>

      {displayMode === 'list' && (
        <>
          <div className="results-list__content">
            {results.map((record, i) => {
              const index = startResult + i
              return <SearchResult key={i} data={record} index={index} lat={lat} lng={lng} />
            })}
          </div>
          <ListPager numPages={numPages} pageIndex={pageIndex} onChange={onPageChange} />
        </>
      )}
      {displayMode === 'map' && (
        <div className="results-list__map">
          <div className="results-list__map-controls">
            <SelectPager
              numPages={numPages}
              pageIndex={pageIndex}
              count={count}
              onChange={onPageChange}
            />
            <Button variant="text" onClick={() => changeDisplayMode('list')}>
              {t('Exit Map View')}
            </Button>
          </div>
          <LeafletMap
            isLoading={isLoading}
            places={mapPlaces}
            selectedPlaceId={selectedResultId}
            center={selectedRecord ? [selectedRecord.Latitude, selectedRecord.Longitude] : null}
            zoomLevel={selectedRecord ? 17 : null}
          />
        </div>
      )}
    </div>
  )
}

export default ResultsList
